import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import { useRef } from 'react'
import { useTheme } from '@mui/material/styles'

import { Text } from '@mxenabled/mxui'
import { Icon } from '@mxenabled/mx-icons'

import { AccountBlockData } from '../../../../core/types/InsightsInstanceSlotData'
import { formatCurrency } from '../../../../core/utils/NumberFormatting'
import { DepositLogo } from '../../../components/ComponentBlocks/DepositLogo'
import { Confetti } from '../../../components/shared/Confetti'
import { ReactComponent as PaidOffCardGraphic } from '../../../components/shared/images/PaidOffCard.svg'
import { ReactComponent as SavingsGraphic } from '../../../components/shared/images/SavingsAccountDeposit.svg'
import { useContainerDimensions } from '../../../hooks/useContainerDimensions'

import type { BlockProps } from '../../types'

import { HeaderComponent } from './HeaderComponent'
import { IconComponent } from './IconComponent'
import { getLayoutConfig } from './layout'

/** Default generic-tile icon per deposit variant, used when no merchant/institution logo. */
const depositIcons: Record<string, string> = {
  UnifiedDeposit: 'payments',
  UnifiedDepositEmergencyFund: 'payments',
  MonthlyObligationsStatusOnTrack: 'monetization_on',
}

const AccountBlock = ({ payload }: BlockProps<AccountBlockData>) => {
  const theme = useTheme()
  const containerRef = useRef<HTMLDivElement>(null)
  const { height, width } = useContainerDimensions(containerRef)
  const { instance_slot, instance_slot_data } = payload
  const { variant } = instance_slot
  const {
    amount,
    sub_text,
    header_text,
    institution_guid,
    institution_name,
    icon_name,
    label,
    merchant_guid,
    merchant_name,
    category_guid,
  } = instance_slot_data
  const layoutConfig = getLayoutConfig(theme)[variant as keyof ReturnType<typeof getLayoutConfig>]

  // Confetti-backed celebration variants (deposits, payoff, savings) — a success-tinted card
  // on a gradient + confetti wash. Everything variant-specific is flat on `layoutConfig`.
  if (layoutConfig?.isConfetti) {
    const graphics: Record<string, React.ReactNode> = {
      paidOffCard: (
        // SVG is 58x46 but the card sits at (0,14) with the check badge overflowing top-right.
        // Box it to the card's real 44x32 footprint and shift the SVG up so it aligns with the text.
        <Box sx={{ flexShrink: 0, height: 32, position: 'relative', width: 44 }}>
          <PaidOffCardGraphic
            aria-hidden={true}
            role="presentation"
            style={{ left: 0, position: 'absolute', top: -14 }}
          />
        </Box>
      ),
      savings: <SavingsGraphic aria-hidden={true} height={55} role="presentation" width={55} />,
      logo: (
        <Box
          sx={{
            borderRadius: '4px',
            flexShrink: 0,
            height: 48,
            overflow: 'hidden',
            width: 48,
          }}
        >
          <DepositLogo
            categoryGuid={category_guid}
            iconName={depositIcons[variant as keyof typeof depositIcons] ?? 'payments'}
            institutionGuid={institution_guid}
            institutionName={institution_name}
            merchantGuid={merchant_guid}
            merchantName={merchant_name}
          />
        </Box>
      ),
    }
    const graphic = graphics[layoutConfig.graphic ?? 'logo']

    // Deposit cards show the payer name — a merchant when we have one, otherwise the institution.
    const secondaryText =
      layoutConfig.secondarySource === 'merchant' ? (merchant_name ?? institution_name) : label
    const secondary = secondaryText ? (
      <Text color={layoutConfig.secondaryColor} variant={layoutConfig.secondaryVariant}>
        {secondaryText}
      </Text>
    ) : null
    const amountText = (
      <Text bold={true} color="success.dark" variant={layoutConfig.amountVariant}>
        {`${layoutConfig.amountPrefix ?? ''}${formatCurrency(amount, '0,0.00')}`}
      </Text>
    )

    return (
      <Box
        ref={containerRef}
        sx={{
          backgroundImage: layoutConfig.gradient,
          bgcolor: 'background.paper',
          overflow: 'hidden',
          position: 'relative',
          p: 16,
          width: '100%',
        }}
      >
        <Box
          sx={{
            inset: 0,
            pointerEvents: 'none',
            position: 'absolute',
            zIndex: 1,
          }}
        >
          <Confetti height={height} shouldRun={height > 0 && width > 0} width={width} />
        </Box>

        <Stack
          sx={{
            borderRadius: '8px',
            overflow: 'hidden',
            position: 'relative',
            width: '100%',
            zIndex: 2,
          }}
        >
          {layoutConfig.categoryStrip && header_text && (
            <Box sx={{ bgcolor: 'success.main', px: 12, py: 4 }}>
              <Text color="success.contrastText" variant="caption">
                {header_text}
              </Text>
            </Box>
          )}

          <Stack
            sx={{
              alignItems: 'center',
              bgcolor: 'success.lighter',
              border: '1px solid',
              borderColor: 'success.main',
              borderRadius: '8px',
              // With a strip above, the body's top corners tuck under it (no double border line).
              ...(layoutConfig.categoryStrip && {
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
                borderTop: 0,
              }),
              flexDirection: 'row',
              ...layoutConfig.rowSx,
            }}
          >
            <Box sx={{ display: 'flex', flexShrink: 0 }}>{graphic}</Box>

            <Stack sx={{ flex: '1 0 0', minWidth: 0 }}>
              {layoutConfig.secondaryPosition === 'above' && secondary}
              {amountText}
              {layoutConfig.secondaryPosition === 'below' && secondary}
            </Stack>
          </Stack>
        </Stack>
      </Box>
    )
  }

  return (
    <Stack
      sx={{
        p: 16,
        ...layoutConfig,
      }}
    >
      {header_text && <HeaderComponent headerText={header_text} />}
      <Stack
        sx={{
          alignItems: 'center',
          bgcolor: 'background.paper',
          borderRadius: '8px',
          ...(header_text && {
            borderTopLeftRadius: '0',
            borderTopRightRadius: '0',
          }),
          gap: 16,
          p: 16,
          width: '100%',
          ...((institution_guid || icon_name) && {
            flexDirection: 'row',
          }),
        }}
      >
        <IconComponent blockData={instance_slot_data} variant={variant} />

        <Stack>
          <Text variant="h1">{formatCurrency(amount, '0,0.00')}</Text>
          <Stack sx={{ gap: 8, flexDirection: 'row', alignItems: 'center' }}>
            {layoutConfig?.sub_logo && (
              <Icon color="warning" fill={true} name={layoutConfig.sub_logo} size={20} />
            )}
            <Text variant="body2">{sub_text}</Text>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  )
}

export default AccountBlock
